/**
 * Content Access Control Utilities
 * Defines access levels for different types of content based on user roles
 */

import { ROLE_NAMES } from '@/lib/constants/roles';

// Content access levels
export enum ContentAccessLevel {
  PUBLIC = 'public',           // GMTV, Devotionals - accessible to all authenticated users
  AUTHENTICATED = 'authenticated', // All public + Worship services, General teaching
  EXTENDED = 'extended',       // All authenticated + Special events, Archived sermons
  LEADERSHIP = 'leadership',   // All extended + Leadership meetings, Admin announcements
  MEDIA = 'media',            // All leadership + Stream management, Analytics
  ADMIN = 'admin'             // Admin-only content
}

// Content categories mapping to access levels
export const CONTENT_CATEGORIES = {
  // Public content
  [ContentAccessLevel.PUBLIC]: ['gmtv', 'devotionals', 'public_announcements'],

  // Authenticated content (extends public)
  [ContentAccessLevel.AUTHENTICATED]: ['gmtv', 'devotionals', 'worship_services', 'general_teaching', 'community_chat'],

  // Extended access (extends authenticated)
  [ContentAccessLevel.EXTENDED]: ['gmtv', 'devotionals', 'worship_services', 'special_events', 'archived_sermons', 'community_chat', 'prayer_requests'],

  // Leadership access (extends extended)
  [ContentAccessLevel.LEADERSHIP]: ['gmtv', 'devotionals', 'worship_services', 'special_events', 'archived_sermons', 'leadership_meetings', 'admin_announcements', 'community_chat', 'prayer_requests'],

  // Media team access (extends leadership)
  [ContentAccessLevel.MEDIA]: ['gmtv', 'devotionals', 'worship_services', 'special_events', 'archived_sermons', 'leadership_meetings', 'admin_announcements', 'stream_management', 'analytics', 'community_chat', 'prayer_requests'],

  // Admin access (extends media)
  [ContentAccessLevel.ADMIN]: ['gmtv', 'devotionals', 'worship_services', 'special_events', 'archived_sermons', 'leadership_meetings', 'admin_announcements', 'stream_management', 'analytics', 'system_admin', 'community_chat', 'prayer_requests']
};

// Role to content access level mapping
export const ROLE_CONTENT_ACCESS: Record<string, ContentAccessLevel[]> = {
  [ROLE_NAMES.MEMBER]: [ContentAccessLevel.PUBLIC, ContentAccessLevel.AUTHENTICATED],
  [ROLE_NAMES.PASTOR]: [ContentAccessLevel.PUBLIC, ContentAccessLevel.AUTHENTICATED, ContentAccessLevel.EXTENDED, ContentAccessLevel.LEADERSHIP],
  [ROLE_NAMES.MEDIA_TEAM]: [ContentAccessLevel.PUBLIC, ContentAccessLevel.AUTHENTICATED, ContentAccessLevel.EXTENDED, ContentAccessLevel.LEADERSHIP, ContentAccessLevel.MEDIA],
  [ROLE_NAMES.MODERATOR]: [ContentAccessLevel.PUBLIC, ContentAccessLevel.AUTHENTICATED, ContentAccessLevel.EXTENDED, ContentAccessLevel.LEADERSHIP, ContentAccessLevel.MEDIA],
  [ROLE_NAMES.ADMIN]: [ContentAccessLevel.PUBLIC, ContentAccessLevel.AUTHENTICATED, ContentAccessLevel.EXTENDED, ContentAccessLevel.LEADERSHIP, ContentAccessLevel.MEDIA, ContentAccessLevel.ADMIN]
};

/**
 * Get the maximum content access level for a user role
 */
export function getMaxContentAccessLevel(userRole: string): ContentAccessLevel {
  const accessLevels = ROLE_CONTENT_ACCESS[userRole] || [ContentAccessLevel.PUBLIC];

  // Return the highest access level
  const levelOrder = [
    ContentAccessLevel.PUBLIC,
    ContentAccessLevel.AUTHENTICATED,
    ContentAccessLevel.EXTENDED,
    ContentAccessLevel.LEADERSHIP,
    ContentAccessLevel.MEDIA,
    ContentAccessLevel.ADMIN
  ];

  for (const level of levelOrder.reverse()) {
    if (accessLevels.includes(level)) {
      return level;
    }
  }

  return ContentAccessLevel.PUBLIC;
}

/**
 * Check if a user role can access specific content categories
 */
export function canAccessContent(userRole: string, contentCategories: string[]): boolean {
  const userAccessLevels = ROLE_CONTENT_ACCESS[userRole] || [ContentAccessLevel.PUBLIC];

  // Get all accessible categories for this user
  const accessibleCategories = userAccessLevels.flatMap(level => CONTENT_CATEGORIES[level]);

  // Check if user can access ANY of the required categories
  return contentCategories.some(category => accessibleCategories.includes(category));
}

/**
 * Get all content categories accessible to a user role
 */
export function getAccessibleContentCategories(userRole: string): string[] {
  const userAccessLevels = ROLE_CONTENT_ACCESS[userRole] || [ContentAccessLevel.PUBLIC];
  return userAccessLevels.flatMap(level => CONTENT_CATEGORIES[level]);
}

/**
 * Determine content access level from stream tags
 */
export function getContentAccessLevelFromTags(tags: string[]): ContentAccessLevel {
  // Check tags for content type indicators
  const contentTypeMap: Record<string, ContentAccessLevel> = {
    'leadership': ContentAccessLevel.LEADERSHIP,
    'leadership_meeting': ContentAccessLevel.LEADERSHIP,
    'admin_announcement': ContentAccessLevel.LEADERSHIP,
    'media_only': ContentAccessLevel.MEDIA,
    'stream_management': ContentAccessLevel.MEDIA,
    'analytics': ContentAccessLevel.MEDIA,
    'admin_only': ContentAccessLevel.ADMIN,
    'system_admin': ContentAccessLevel.ADMIN,
    'special_event': ContentAccessLevel.EXTENDED,
    'archived_sermon': ContentAccessLevel.EXTENDED,
    'prayer_request': ContentAccessLevel.EXTENDED,
    'worship_service': ContentAccessLevel.AUTHENTICATED,
    'general_teaching': ContentAccessLevel.AUTHENTICATED,
    'devotional': ContentAccessLevel.PUBLIC,
    'gmtv': ContentAccessLevel.PUBLIC,
    'public_announcement': ContentAccessLevel.PUBLIC
  };

  // Find the highest access level required by any tag
  let maxLevel = ContentAccessLevel.PUBLIC;
  const levelOrder = [
    ContentAccessLevel.PUBLIC,
    ContentAccessLevel.AUTHENTICATED,
    ContentAccessLevel.EXTENDED,
    ContentAccessLevel.LEADERSHIP,
    ContentAccessLevel.MEDIA,
    ContentAccessLevel.ADMIN
  ];

  for (const tag of tags) {
    const level = contentTypeMap[tag.toLowerCase()];
    if (level && levelOrder.indexOf(level) > levelOrder.indexOf(maxLevel)) {
      maxLevel = level;
    }
  }

  return maxLevel;
}

/**
 * Check if a user can access a stream based on its tags and user role
 */
export function canUserAccessStream(userRole: string, streamTags?: string[] | null): boolean {
  if (!streamTags || streamTags.length === 0) {
    // No tags means public content
    return true;
  }

  const contentLevel = getContentAccessLevelFromTags(streamTags);
  const userAccessLevels = ROLE_CONTENT_ACCESS[userRole] || [ContentAccessLevel.PUBLIC];

  return userAccessLevels.includes(contentLevel);
}